import React, { useState, useEffect } from 'react';
import * as api from '../api';

function Admin({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'user' });
  const [newPassword, setNewPassword] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const fetchUsers = async () => {
    try {
      const data = await api.getUsers();
      setUsers(data);
      setLoading(false);
    } catch (err) {
      setError('Could not load users.');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleAddUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      setError('All fields are required.');
      return;
    }
    try {
      const data = await api.createUser(newUser);
      if (data.error) {
        setError(data.error);
        return;
      }
      setShowAddModal(false);
      setNewUser({ name: '', email: '', password: '', role: 'user' });
      fetchUsers();
      showSuccess('User added successfully.');
    } catch (err) {
      setError('Server error.');
    }
  };

  const handleDeleteUser = async (id) => {
    try {
      await api.deleteUser(id);
      fetchUsers();
      setDeleteConfirm(null);
      showSuccess('User deleted successfully.');
    } catch (err) {
      setError('Server error.');
    }
  };

  const handleChangePassword = async (id) => {
    if (!newPassword) return;
    try {
      await api.updatePassword(id, newPassword);
      setShowPasswordModal(null);
      setNewPassword('');
      showSuccess('Password updated successfully.');
    } catch (err) {
      setError('Server error.');
    }
  };

  if (currentUser.role !== 'admin') {
    return (
      <div style={{ textAlign: 'center', padding: '80px 0' }}>
        <p style={{ fontSize: '48px' }}>🚫</p>
        <h2 style={{ color: '#1a1a2e' }}>Access Denied</h2>
        <p style={{ color: '#718096' }}>You need admin access to view this page.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>Admin Panel</h1>
        <p>Manage user accounts. Only admins can access this page.</p>
      </div>

      {successMessage && (
        <div style={{
          backgroundColor: '#f0fff4',
          border: '1px solid #9ae6b4',
          borderRadius: '6px',
          padding: '12px 16px',
          marginBottom: '20px',
          color: '#276749',
          fontSize: '13px'
        }}>
          ✅ {successMessage}
        </div>
      )}

      {error && (
        <div style={{
          backgroundColor: '#fff5f5',
          border: '1px solid #fed7d7',
          borderRadius: '6px',
          padding: '12px 16px',
          marginBottom: '20px',
          color: '#c53030',
          fontSize: '13px'
        }}>
          {error}
        </div>
      )}

      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={() => { setShowAddModal(true); setError(''); }}
          style={{
            backgroundColor: '#4299e1',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            padding: '10px 24px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          + Add User
        </button>
      </div>

      <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#2d3748' }}>
              <th style={{ padding: '12px 16px', textAlign: 'left', color: '#e2e8f0', fontSize: '13px' }}>Name</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', color: '#e2e8f0', fontSize: '13px' }}>Email</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', color: '#e2e8f0', fontSize: '13px' }}>Role</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', color: '#e2e8f0', fontSize: '13px' }}>Created</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', color: '#e2e8f0', fontSize: '13px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} style={{ padding: '24px', textAlign: 'center', color: '#718096' }}>Loading...</td>
              </tr>
            ) : (
              users.map((user, index) => (
                <tr key={user.id} style={{ backgroundColor: index % 2 === 0 ? 'white' : '#f8fafc' }}>
                  <td style={{ padding: '12px 16px', fontSize: '14px', color: '#1a1a2e', fontWeight: '500' }}>
                    {user.name}
                    {user.id === currentUser.id && (
                      <span style={{ marginLeft: '8px', fontSize: '11px', color: '#4299e1' }}>(you)</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '14px', color: '#4a5568' }}>{user.email}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{
                      backgroundColor: user.role === 'admin' ? '#1a1a2e' : '#ebf8ff',
                      color: user.role === 'admin' ? 'white' : '#2b6cb0',
                      borderRadius: '12px',
                      padding: '2px 10px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      {user.role}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', color: '#718096' }}>
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => { setShowPasswordModal(user.id); setNewPassword(''); }}
                        style={{
                          background: 'none',
                          border: '1px solid #e2e8f0',
                          borderRadius: '5px',
                          padding: '4px 10px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          color: '#4a5568'
                        }}
                      >
                        🔑 Password
                      </button>
                      <select
                        value={user.role}
                        onChange={async e => {
                          await api.updateUserRole(user.id, e.target.value);
                          fetchUsers();
                        }}
                        style={{
                          border: '1px solid #e2e8f0',
                          borderRadius: '5px',
                          padding: '4px 6px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          color: '#4a5568',
                          backgroundColor: 'white'
                        }}
                      >
                        <option value="admin">Admin</option>
                        <option value="editor">Editor</option>
                        <option value="viewer">Viewer</option>
                      </select>
                      {user.id !== currentUser.id && (
                        <button
                          onClick={() => setDeleteConfirm(user.id)}
                          style={{
                            background: 'none',
                            border: '1px solid #fed7d7',
                            borderRadius: '5px',
                            padding: '4px 10px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            color: '#e53e3e'
                          }}
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ width: '440px' }}>
            <h2 style={{ marginBottom: '20px', color: '#1a1a2e', fontSize: '20px' }}>Add New User</h2>
            {error && <p style={{ color: '#c53030', fontSize: '13px', marginBottom: '12px' }}>{error}</p>}
            {['name', 'email', 'password'].map(field => (
              <div key={field} style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#4a5568', marginBottom: '6px' }}>
                  {field.charAt(0).toUpperCase() + field.slice(1)} *
                </label>
                <input
                  type={field === 'password' ? 'password' : field === 'email' ? 'email' : 'text'}
                  value={newUser[field]}
                  onChange={e => setNewUser(prev => ({ ...prev, [field]: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            ))}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#4a5568', marginBottom: '6px' }}>
                Role
              </label>
              <select
                value={newUser.role}
                onChange={e => setNewUser(prev => ({ ...prev, role: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0',
                  fontSize: '14px',
                  outline: 'none'
                }}
              >
                <option value="editor">Editor</option>
                <option value="viewer">Viewer</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => { setShowAddModal(false); setError(''); }}
                style={{
                  flex: 1, padding: '10px', borderRadius: '6px',
                  border: '1px solid #e2e8f0', background: 'white',
                  cursor: 'pointer', fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddUser}
                style={{
                  flex: 1, padding: '10px', borderRadius: '6px',
                  border: 'none', background: '#4299e1', color: 'white',
                  cursor: 'pointer', fontSize: '14px', fontWeight: '600'
                }}
              >
                Add User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ width: '400px' }}>
            <h2 style={{ marginBottom: '20px', color: '#1a1a2e', fontSize: '20px' }}>Change Password</h2>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#4a5568', marginBottom: '6px' }}>
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                autoFocus
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowPasswordModal(null)}
                style={{
                  flex: 1, padding: '10px', borderRadius: '6px',
                  border: '1px solid #e2e8f0', background: 'white',
                  cursor: 'pointer', fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleChangePassword(showPasswordModal)}
                style={{
                  flex: 1, padding: '10px', borderRadius: '6px',
                  border: 'none', background: '#4299e1', color: 'white',
                  cursor: 'pointer', fontSize: '14px', fontWeight: '600'
                }}
              >
                Update Password
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ width: '400px', textAlign: 'center' }}>
            <p style={{ fontSize: '40px', marginBottom: '12px' }}>🗑️</p>
            <h3 style={{ marginBottom: '8px', color: '#1a1a2e' }}>Delete User?</h3>
            <p style={{ color: '#718096', fontSize: '14px', marginBottom: '24px' }}>
              This will permanently delete the user account.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => setDeleteConfirm(null)}
                style={{
                  padding: '10px 24px', borderRadius: '6px',
                  border: '1px solid #e2e8f0', background: 'white',
                  cursor: 'pointer', fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteUser(deleteConfirm)}
                style={{
                  padding: '10px 24px', borderRadius: '6px',
                  border: 'none', background: '#e53e3e', color: 'white',
                  cursor: 'pointer', fontSize: '14px', fontWeight: '600'
                }}
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Admin;